<template>
  <div class="ux-submission-form" :class="classes">
    <template v-if="state === 'form.error'">
      <slot name="error" v-bind="{ error, softReset }">
        <h3>An error occurred</h3>
        <pre v-if="error.message">{{ error.message }}</pre>
        <div v-if="error.data?.errors">
          <pre :key="field.path" v-for="field in error.data.errors">{{
            field.message
          }}</pre>
        </div>
        <pre v-else>{{ error }}</pre>
        <ux-button color="primary" @click="softReset">
          Try again
          <ux-icon right icon="fa-undo" />
        </ux-button>
      </slot>
    </template>
    <template v-else-if="state === 'form.complete'">
      <slot name="default" :reset="reset">
        Thank you
        <ux-button @click="reset"> Back to form </ux-button>
      </slot>
    </template>
    <template v-else-if="state === 'form.intent'">
      <template v-if="intentModule === 'stripe'">
        <ux-panel>
          <ux-panel-body> Total: {{ formattedTotal }} </ux-panel-body>
        </ux-panel>
        <!-- <ux-panel> -->
        <!-- <ux-panel-body> -->
        <StripeElements
          v-if="stripeIntent"
          v-slot="{ elements, instance }"
          ref="elms"
          :stripe-key="stripeIntent.key"
          :instance-options="stripeIntent.instanceOptions"
          :elements-options="stripeIntent.elementOptions"
        >
          <StripeElement
            ref="stripePayment"
            type="payment"
            :elements="elements"
          >
          </StripeElement>
        </StripeElements>
        <div class="intent-submit">
          <ux-button color="primary" @click="confirmIntention">
            Confirm Payment
          </ux-button>
        </div>
        <!-- </ux-panel-body> -->
        <!-- </ux-panel> -->
      </template>
    </template>
    <template v-else>
      <div class="multistep-form" v-if="multistep">
        <div class="multistep-form-sections">
          <div
            class="multistep-form-section"
            :class="{
              inactive: index !== currentPage,
              past: index < currentPage,
              future: index > currentPage,
              active: index === currentPage,
            }"
            v-for="(page, index) in pages"
          >
            <div class="multistep-fields">
              <ux-form
                :step="index + 1"
                submission
                :sandbox="sandbox"
                ref="form"
                :paymentConfiguration="paymentConfiguration"
                v-model="model"
                @form:state="multiFormStateUpdated($event, index)"
                :fields="[page]"
              />
            </div>

            <div class="multistep-controls">
              <flex-row gap center>
                <flex-cell shrink v-if="previousPageEnabled">
                  <ux-button link @click="previousPage"> Back </ux-button>
                </flex-cell>
                <flex-cell />

                <flex-cell shrink v-if="nextPageEnabled">
                  <span
                    class="tooltip-span"
                    :tooltip="
                      currentFormIsInvalid
                        ? 'Please fix errors to continue'
                        : null
                    "
                  >
                    <ux-button
                      color="primary"
                      @click="nextPageAttempt"
                      :disabled="currentFormIsInvalid"
                    >
                      Next <ux-icon icon="fa-angle-right" right />
                    </ux-button>
                  </span>
                </flex-cell>

                <flex-cell shrink v-if="submittable">
                  <span :tooltip="tooltip">
                    <ux-button
                      :disabled="buttonDisabled"
                      color="primary"
                      @click="submit"
                      :loading="processing"
                    >
                      {{ submitText }}
                    </ux-button>
                  </span>
                </flex-cell>
              </flex-row>
            </div>
          </div>
        </div>

        <div class="multistep-progress">
          {{ displayCurrentPage }} of {{ displayTotalPages }}

          <ux-progress-bar :value="displayPercentage" />
        </div>
      </div>
      <template v-else>
        <ux-form
          submission
          :sandbox="sandbox"
          ref="form"
          :paymentConfiguration="paymentConfiguration"
          v-model="model"
          @form:state="formStateUpdated"
          :fields="formFields"
        />

        <div v-if="invalidFields.length" class="invalid-field-message">
          <h5>Please fix the issues below to continue</h5>
          <div v-for="field in invalidFields" :key="field.key">
            {{
              field.message ||
              `Please provide a valid answer for ${field.title}`
            }}
          </div>
        </div>

        <span :tooltip="tooltip">
          <ux-button
            :disabled="buttonDisabled"
            color="primary"
            @click="submit"
            :loading="processing"
          >
            {{ submitText }}
          </ux-button>
        </span>
      </template>
    </template>

    <!-- <a @click="touch()">Touch</a> |
        <a @click="untouch()">Untouch</a> |
        <a @click="reset()">Reset</a> -->
  </div>
</template>
<script>
import { ref } from "vue";
import UxForm from "./form.vue";
import debounce from "lodash/debounce";

const STATE_READY = "form.ready";
const STATE_PROCESSING = "form.processing";
const STATE_COMPLETE = "form.complete";
const STATE_ERROR = "form.error";
const STATE_INTENT = "form.intent";

import { StripeElements, StripeElement } from "vue-stripe-js";

export default {
  emits: ["reset", "processing", "intent", "success", "error"],
  methods: {
    multiFormStateUpdated(state, index) {
      if (index === this.currentPage) {
        this.formState = state;

        if (this.currentFormIsInvalid) {
          this.checkPageValidity();
        }
      }
    },
    formStateUpdated(state) {
      this.formState = state;

      if (this.currentFormIsInvalid) {
        this.checkPageValidity();
      }
    },
    softReset() {
      this.processing = false;
      this.state = STATE_READY;
    },
    touch() {
      if (this.$refs.form) {
        if (Array.isArray(this.$refs.form)) {
          this.$refs.form.forEach(function (form) {
            form.touch();
          });
        } else {
          this.$refs.form.touch();
        }
      }
    },
    untouch() {
      if (this.$refs.form) {
        if (Array.isArray(this.$refs.form)) {
          this.$refs.form.forEach(function (form) {
            form.untouch();
          });
        } else {
          this.$refs.form.untouch();
        }
      }
    },
    reset() {
      this.model = {};
      this.intent = null;
      this.currentPage = 0;
      if (this.$refs.form) {
        if (Array.isArray(this.$refs.form)) {
          this.$refs.form.forEach(function (form) {
            form.reset();
          });
        } else {
          this.$refs.form.reset();
        }
      }

      this.state = STATE_READY;
      this.error = null;
      this.submitAttempted = false;
      this.processing = false;
      this.$emit("reset");
    },
    async confirmIntention() {
      const self = this;
      self.processing = true;
      self.$emit("processing");

      // Get the form intention id
      const intentionID = self.$sdk.utils.id(self.intent);

      ////////////////////////////////////////

      const elms = self.$refs.elms;
      const paymentElement = self.$refs.stripePayment;
      const return_url = window.location.href;

      // Confirm the payment intention
      const result = await elms.instance.confirmPayment({
        elements: paymentElement.elements,
        redirect: "if_required",
        confirmParams: {
          return_url,
        },
      });

      self.$sdk.api
        .post(`/form/capture/${intentionID}`)
        .then(captureComplete, captureFailed);

      async function captureComplete({ data }) {
        // Run post submission functions
        await self.postSubmit(data);
        // Set the state to ready
        self.processing = false;
        self.state = STATE_COMPLETE;
        self.$emit("success", data);
      }

      async function captureFailed(err) {
        err = err.response?.data || err;
        self.error = err;
        self.processing = false;
        self.state = STATE_ERROR;
        self.$emit("error", err);
      }

      // , {
      // data: submission,
      //     sandbox: self.sandbox,
      // })
      // .then(function(res) {

      // });

      // console.log('ELEMS', {window, elms, paymentElement});

      // , { payment_method: { card: cardNumber } })
    },
    async submit() {
      const self = this;
      self.touch();

      if (!self.submitAttempted) {
        self.submitAttempted = true;
        self.touch();
      }

      if (self.buttonDisabled) {
        console.log("Form is invalid", this.invalidFields);
        return;
      }

      self.processing = true;
      self.state = STATE_PROCESSING;
      self.$emit("processing");

      // Run pre submission functions

      // Create the submission
      const { submission, error } = await self.preSubmit(self.model);

      if (error) {
        console.log("pre submit error", error);
        return submissionFailed(error);
      }

      // Include GTM Data
      const gtm = {};
      const searchParams = new URLSearchParams(window.location.search);
      for (const [key, value] of searchParams) {
        gtm[key] = value;
      }

      // Submit to the server
      self.$sdk.api
        .post(`/form/${self.formID}`, {
          data: submission,
          sandbox: self.sandbox,
          gtm, // The GTM tag (If applicable)
          request: self.$sdk.utils.id(self.formRequest), // The form request id (If applicable)
          event: self.$sdk.utils.id(self.event), // The event id if applicable
        })
        .then(function (res) {
          const { data } = res;
          if (data.submission) {
            return submissionComplete(res.data);
          }

          if (data.intent) {
            return submissionIntentReturned(res.data);
          }

          return submissionFailed({
            message: "Unexpected response from server",
            status: 500,
            statusCode: 500,
          });
        }, submissionFailed);

      async function submissionIntentReturned({ intent }) {
        console.log("Submission intent was returned", intent);
        // Save the intent to the scope
        self.intent = intent;

        // Figure out how to handle it
        switch (self.intentModule) {
          case "stripe":
            await self.$sdk.utils.loadExternalScript(
              "https://js.stripe.com/v3/",
            );
            break;
        }

        // Change the state
        self.state = STATE_INTENT;
        self.processing = false;
        self.$emit("intent", intent);
      }

      async function submissionComplete(response) {
        // Run post submission functions
        await self.postSubmit(response);
        // Set the state to ready
        self.processing = false;
        self.state = STATE_COMPLETE;
        self.$emit("success", response);
      }

      async function submissionFailed(err) {
        err = err.response?.data || err;
        self.error = err;
        self.processing = false;
        self.state = STATE_ERROR;
        self.$emit("error", err);
      }
    },

    async preSubmit(model) {
      const self = this;

      // Run through payments
      const submission = JSON.parse(JSON.stringify(model));
      const total = submission._payment?.total;
      const gateway = submission._payment?.gateway;

      // If the form has geographic requirements
      if (self.form.hasGeographicRequirements) {
        // Get the user's location before we submit
        const { coords } = await self.$sdk.geo
          .getPosition()
          .catch(function (err) {
            console.log("Error", err);
            return {
              coords: undefined,
            };
          });

        if (!coords || !coords.latitude || !coords.longitude) {
          return {
            error: {
              message:
                "Location data is required to submit this form. Please allow location to continue.",
              status: 400,
              statusCode: 400,
            },
          };
        }

        submission._geo = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          altitude: coords.altitude,
          accuracy: coords.accuracy,
        };
      }

      if (!total) {
        return { submission };
      }

      if (!gateway) {
        return {
          error: {
            message: "No gateway selected",
            status: 400,
            statusCode: 400,
          },
        };
      }

      // const paymentElement = this.payment.element;
      const paymentIntegration = gateway.integration;
      // const paymentModule = paymentIntegration?.module;

      // if (!paymentModule) {
      //     return {
      //         error: {
      //             message: 'Invalid payment module',
      //             status: 400,
      //             statusCode: 400,
      //         }
      //     };
      // }

      // switch (paymentModule) {
      // case 'stripe':
      //     const stripeElementsInstance = paymentElement?.$refs?.elms?.instance;
      //     const cardElement = paymentElement?.$refs?.card?.stripeElement;

      //     if (stripeElementsInstance && cardElement) {
      //         const result = await stripeElementsInstance.createToken(cardElement)

      //         if(result.error) {
      //             return {
      //                 error:result.error
      //             }
      //         }

      //         if(!result.token) {
      //             return {
      //                 error:{
      //                     message:'Unable to generate secure token',
      //                     status:500,
      //                     statusCode:500,
      //                 }
      //             }
      //         }

      //         // Update with the payment token
      //        submission._payment.source = result.token;
      //     }
      //     break;
      // }

      // console.log('GENERATE PAYMENT', {
      //     total,
      //     gateway,
      //     paymentConfiguration: self.paymentConfiguration,
      //     submission,
      // });

      return { submission };
    },
    async postSubmit(response) {},
    checkPageValidity() {
      const currentPageForm = this.$refs.form[this.currentPage];
      currentPageForm.touch();
      this.currentFormIsInvalid = currentPageForm.invalid;
    },
    nextPageAttempt() {
      this.checkPageValidity();

      if (this.currentFormIsInvalid) {
        // We're not valid so we can't continue
        return;
      }

      this.nextPage();
    },
    nextPage() {
      this.currentPage++;
      this.$nextTick(function () {
        this.touch();
        this.$nextTick(function () {
          this.untouch();
        });
      });
    },
    previousPage() {
      this.movedBackwards = true;
      this.currentPage--;

      this.$nextTick(function () {
        this.movedBackwards = false;
        this.checkPageValidity();
      });
    },
  },
  components: {
    UxForm,
    StripeElements,
    StripeElement,
  },
  provide() {
    return {
      form: this.form,
      // payment: this.payment,
    };
  },
  props: {
    submitText: {
      type: String,
      default() {
        return "Submit";
      },
    },
    modelValue: {
      type: Object,
      default() {
        return {};
      },
    },
    event: {
      type: String,
    },
    formRequest: {
      type: String,
    },
    form: {
      type: Object,
      required: true,
    },
    multistep: {
      type: Boolean,
      default: false,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    autoadvance: {
      type: Boolean,
      default: false,
    },
    sandbox: {
      type: Boolean,
      default: false,
    },
    showBlockers: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    currentPage(i) {
      const currentPageForm = this.$refs.form[i];
      const currentFormState = currentPageForm.getCurrentFormState();
      this.formState = currentFormState;
      // console.log(" updated form state", currentFormState, i);
      // if (this.currentFormIsInvalid) {
      //   this.checkPageValidity();
      // }
      this.$nextTick(function () {
        currentPageForm.$el.scrollIntoView({ smooth: true });
      });
    },
    canAdvance(yes) {
      if (this.movedBackwards) {
        return;
      }
      // If all fields are valid
      if (yes) {
        // And we are set to auto advance
        if (this.autoadvance) {
          const currentPageForm = this.$refs.form[this.currentPage];
          const currentPageFormFields = currentPageForm?.fields || [];
          const flatFields = this.$sdk.utils
            .mapFields(currentPageFormFields)
            .filter(function ({ type }) {
              switch (type) {
                case "void":
                  return false;
              }

              return true;
            });

          console.log("Lets autoadvance!!!");
          // And there is only one fillable field
          if (flatFields.length == 1) {
            // Move forward
            this.nextPageAttempt();
          }
        }
      }
    },
    modelValue(val, old) {
      this.model = val;
    },
    model(m) {
      this.$emit("update:modelValue", m);
    },
  },
  data() {
    return {
      movedBackwards: false,
      submitAttempted: false,
      formState: null,
      state: STATE_READY,
      mounted: false,
      model: this.modelValue,
      error: null,
      intent: null,
      processing: false,
      actualCurrentPage: 0,
      currentFormIsInvalid: false,
      // payment: ref({}),
    };
  },
  mounted() {
    this.mounted = true;
  },
  beforeUnmount() {
    this.mounted = false;
  },

  computed: {
    invalidFields() {
      if (!this.showBlockers || !this.submitAttempted) {
        return [];
      }

      if (this.buttonDisabled) {
        return this.formState?.invalidFields || [];
      } else {
        return [];
      }
    },
    canAdvance() {
      // Return true if the state is valid
      return !!this.formState?.valid;
    },
    paymentEnabled() {
      return !!this.paymentConfiguration?.enabled;
    },
    requiresPayment() {
      if (!this.paymentEnabled) {
        return false;
      }

      return parseInt(this.model._payment?.total) > 0;
    },
    currentPageFields() {
      return [this.pages[this.actualCurrentPage]];
    },
    pages() {
      return this.formFields;
    },
    currentPage: {
      get() {
        return this.actualCurrentPage;
      },
      set(c) {
        if (c > this.totalPages) {
          c = this.totalPages;
        }

        if (c < 0) {
          c = 0;
        }

        this.actualCurrentPage = c;
      },
    },
    totalPages() {
      return this.pages.length - 1;
    },

    displayCurrentPage() {
      return this.currentPage + 1;
    },
    displayTotalPages() {
      return this.totalPages + 1;
    },
    displayPercentage() {
      return Math.round(
        (this.displayCurrentPage / this.displayTotalPages) * 100,
      );
    },
    previousPageEnabled() {
      return this.currentPage > 0;
    },
    nextPageEnabled() {
      const morePages = this.currentPage < this.totalPages;

      if (!morePages) {
        return false;
      }

      if (this.autoadvance) {
        if (!this.canAdvance) {
          return false;
        }
      }

      return true;
    },

    submittable() {
      return this.currentPage === this.totalPages;
    },
    payment() {
      return this.model._payment;
    },
    total() {
      return this.payment?.total;
    },
    formattedTotal() {
      return this.$sdk.utils.formatCurrency(
        this.payment.total,
        this.payment.currency,
      );
    },
    intentModule() {
      return this.intentAction.module;
    },
    intentAction() {
      return this.intent?.actionDetails || {};
    },
    stripeIntent() {
      if (this.intentModule !== "stripe") {
        return;
      }

      const clientSecret = this.intentAction?.paymentIntent?.client_secret;
      return {
        module: "stripe",
        key: this.intentAction.key,
        clientSecret,
        instanceOptions: {
          clientSecret,
        },
        elementOptions: {
          clientSecret,
        },
        // paymentElementOptions: {
        //     client_secret: this.intentAction?.paymentIntent?.client_secret,
        // }
      };
    },

    formFields() {
      const output = [...this.fields];

      // If payment is enabled for this form
      if (this.paymentEnabled) {
        // Add in the payment field
        // at the end of the form
        output.push({
          title: "Payment",
          type: "object",
          key: "_payment",
          widget: "payment",
          asObject: true,
          minimum: 1,
          maximum: 1,
        });
      }

      return output;
    },
    classes() {
      const self = this;
      const array = [];

      if (this.formID) {
        array.push(`form-${this.formID}`);
      }

      return array;
    },
    tooltip() {
      return this.buttonDisabled
        ? "Please check the errors in your form"
        : undefined;
    },
    buttonDisabled() {
      // Disable Submission if we try to click and it's invalid

      if (this.preview) {
        return true;
      }

      if (this.noGatewaySelectedButPaymentIsRequired) {
        console.error(
          "There is no payment gateway selected but payment is required",
        );
        return true;
      }

      return this.submitAttempted && this.invalid;
    },
    noGatewaySelectedButPaymentIsRequired() {
      if (!this.total) {
        return false;
      }

      return !this.payment.gateway;
    },
    invalid() {
      var formInvalid = this.formState?.invalid;
      if (formInvalid) {
        console.error("Form is invalid", this.formState.invalid);
        return true;
      }

      return this.noGatewaySelectedButPaymentIsRequired;
    },
    formID() {
      return this.$sdk.utils.id(this.form);
    },
    paymentConfiguration() {
      return this.form?.paymentConfiguration || {};
    },
    fields() {
      return this.form?.fields || [];
    },
  },
};
</script>
<style lang="scss" scoped>
.intent-submit {
  margin-top: 1em;
}

[tooltip] {
  position: relative;
  cursor: help;
  display: block;
}

[tooltip]::after {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  content: attr(tooltip);
  left: 0;
  top: calc(100% + 10px);
  border-radius: 3px;
  box-shadow: 0 0 5px 2px rgba(100, 100, 100, 0.6);
  background-color: var(--bg1, #fff);
  color: inherit;
  z-index: 10;
  padding: 8px;
  width: 300px;
  transform: translateY(-20px);
  transition: all 150ms cubic-bezier(0.25, 0.8, 0.25, 1);
}

[tooltip]:hover::after {
  opacity: 1;
  transform: translateY(0);
  transition-duration: 300ms;
}

.invalid-field-message {
  border: red;
  background: rgba(red, 0.1);
  color: red;
  font-size: 0.7em;
  padding: 0.3em 0.5em;
  border-radius: 0 1em 1em;
  margin: 0.3em 0 1em;
  display: block;
}

.multistep-form-wrapper {
  display: grid;
  position: relative;
}

.multistep-form-section {
  transition:
    opacity 0.8s cubic-bezier(0.25, 0.8, 0.25, 1),
    translate 0.8s cubic-bezier(0.25, 0.8, 0.25, 1);
  opacity: 0;
  grid-area: 1 / 1 / 2 / 2;

  :deep(button) {
    transition:
      opacity 0.7s cubic-bezier(0.25, 0.8, 0.25, 1),
      translate 0.7s cubic-bezier(0.25, 0.8, 0.25, 1);

    @for $i from 1 through 10 {
      &:nth-child(#{$i}) {
        transition-delay: ($i + 1) * 0.2s;
      }
    }
  }

  &.inactive {
    pointer-events: none;
    opacity: 0;
    position: absolute; /* Remove inactive section from normal flow */
    top: 0;
    left: 0;
    width: 100%;

    :deep(button) {
      opacity: 0;
      translate: 0 2em;
    }
  }

  &.active {
    opacity: 1;
    translate: 0 0;
    position: relative; /* Keep active section in normal flow */

    :deep(button) {
      opacity: 1;
      translate: 0 0;
    }
  }

  &.past {
    translate: -3em 0;
  }

  &.future {
    translate: 3em 0;
  }
}
</style>
